import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  Box, Typography, Card, CardContent, Grid, Chip, Avatar, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
} from '@mui/material';
import {
  ArrowBack as BackIcon, Person as PersonIcon,
} from '@mui/icons-material';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export default function PatientHistory() {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API_URL}/api/v1/superadmin/patients/${patientId}/history`)
      .then((res) => setData(res.data))
      .catch(() => toast.error('Failed to load patient history'))
      .finally(() => setLoading(false));
  }, [patientId]);

  if (loading) return <Typography>Loading…</Typography>;
  if (!data) return <Typography>Patient not found.</Typography>;

  const { patient, visits } = data;

  return (
    <Box>
      <Button startIcon={<BackIcon />} onClick={() => navigate('/patients')} sx={{ mb: 2 }}>
        Back to Patients
      </Button>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}><PersonIcon fontSize="large" /></Avatar>
        <Box>
          <Typography variant="h4" fontWeight={700}>{patient.full_name}</Typography>
          <Typography variant="body2" color="text.secondary">{patient.email}</Typography>
        </Box>
      </Box>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary">Clinic</Typography>
              <Typography fontWeight={600}>{patient.clinic_name || 'Unassigned'}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary">Provider</Typography>
              <Typography fontWeight={600}>{patient.provider_name || 'Unassigned'}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary">Date of Birth</Typography>
              <Typography fontWeight={600}>{patient.date_of_birth || '—'}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>Visit History ({visits.length})</Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Provider</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Note</TableCell>
              <TableCell>ICD-10 Codes</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visits.length === 0 ? (
              <TableRow><TableCell colSpan={5}>No visits yet.</TableCell></TableRow>
            ) : visits.map((v) => (
              <TableRow key={v.session_id} hover>
                <TableCell>{v.started_at ? new Date(v.started_at).toLocaleDateString() : '—'}</TableCell>
                <TableCell>{v.provider_name || '—'}</TableCell>
                <TableCell>
                  <Chip size="small" label={v.status}
                    color={v.status === 'finalized' ? 'success' : v.status === 'approved' ? 'info' : 'default'} />
                </TableCell>
                <TableCell>{v.has_note ? 'Yes' : 'No'}</TableCell>
                <TableCell>
                  {(v.icd10_codes || []).length > 0
                    ? v.icd10_codes.map((c, i) => (
                        <Chip key={i} size="small" label={c.code || c} sx={{ mr: 0.5, mb: 0.5 }} />
                      ))
                    : '—'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
